import * as React from 'react';
import cx from 'classnames';
import { useStaticQuery, graphql } from 'gatsby';

import { Divider } from '../../../../components/Divider/Divider';
import { Link } from '../../../../components/Link/Link';
import { Clickable } from '../../../../components/Clickable/Clickable';
import { Legal } from '../../../../components/Legal/Legal';
import { ThemeSentinel } from '../../../../components/ThemeSentinel/ThemeSentinel';
import { toKebabCase } from '../../../../utils/string';
import { useSiteMetadata } from '../../../../utils/useSiteMetadata';

import * as cs from './Footer.module.css';

interface Props {
  page: string;
}

interface ICountryOffices {
  country: string;
  phone: string;
}

const COMPANY_LINKS = [
  { key: 'wellfound', label: 'Careers' },
  { key: 'japanese', label: '日本語版', lang: 'ja' },
];

export const Footer = ({ page }: Props) => {
  const siteMetadata = useSiteMetadata();

  const mainPage = page === 'main';

  const { snippet } = useStaticQuery(graphql`
    query {
      snippet(slug: { eq: "footer" }) {
        id
        data
      }
    }
  `);

  const { credentials, offices } = snippet.data;

  const countryOffices: ICountryOffices[] = [];
  offices.forEach((office: any) => {
    if (!countryOffices.find((item) => item.country === office.country)) {
      countryOffices.push({
        country: office.country,
        phone: office.phone,
      });
    }
  });

  return (
    <footer
      className={cx(cs.wrapper, { [cs.wrapperMain]: mainPage })}
      data-theme-reset={!mainPage}
    >
      {mainPage && <ThemeSentinel />}
      {mainPage ? (
        <div className={cs.bg} />
      ) : (
        <div className={cs.divider}>
          <Divider />
        </div>
      )}

      <div className={cx(cs.footer, { [cs.footerMain]: mainPage })}>
        <div className={cx(cs.containerLine, cs.containerLineContacts)}>
          <Link
            className={cs.aloneLink}
            trackEvent="contact-us-email"
            to={`mailto:${siteMetadata.email}`}
          >
            {siteMetadata.email}
          </Link>
          {countryOffices.map((item: any, i) => (
            <div className={cs.phoneContainer} key={`${item.country}-${i}`}>
              <h3>{item.country}</h3>

              <p>
                <Link
                  trackEvent="footer-office-phone-click"
                  trackLabel={toKebabCase(item.country)}
                  to={`tel:${item.phone.replace(/\s/g, '')}`}
                >
                  {item.phone}
                </Link>
              </p>
            </div>
          ))}
        </div>
        <div className={cs.containerLine}>
          <ul className={cs.companyLinks}>
            <li>
              <Clickable
                className={cs.linkWrapper}
                activeClassName={cs.activeLink}
                to="/contact-us"
                trackEvent="footer-socials-click"
                trackLabel="contact-us-link"
              >
                <span className={cs.link}>Contact us</span>
              </Clickable>
            </li>
            {COMPANY_LINKS.map((entry) => (
              <li key={entry.key}>
                <Clickable
                  className={cs.linkWrapper}
                  activeClassName={cs.active}
                  to={siteMetadata[entry.key]}
                  target="_blank"
                  trackEvent="footer-socials-click"
                  trackLabel={entry.key}
                  rel="noopener noreferrer me"
                  lang={entry.lang}
                >
                  <span className={cs.link}>{entry.label || entry.key}</span>
                </Clickable>
              </li>
            ))}
          </ul>
          <div className={cs.legalContainer}>
            <Legal />
          </div>
          {credentials.map((credential: any) => (
            <p key={credential} className={cs.credential}>
              {credential}
            </p>
          ))}
        </div>
      </div>
    </footer>
  );
};
